"use client";

/**
 * Horizontal quick-filter bar for the listings browse page (bina.az-style).
 *
 * Bar chips (in order):
 *   1. Transaction    - sale / rent / both
 *   2. Listing kind   - multi-select: new build / old build / house
 *                       writes `listing_kind[]` to the URL
 *   3. Rooms          - multi-select pill toggles
 *   4. Price          - commit-on-close range inputs
 *   5. Location       - ListingsLocationPicker (its own modal)
 *   + Advanced "Filtrlər" button
 *
 * Property type, Building type, Area, and Deal tier chips are REMOVED
 * from this bar; they live only inside the advanced modal.
 *
 * Hybrid commit model: quick chips apply instantly via onMerge (which
 * preserves view/sort/everything else). Price commits when its popover
 * closes. The advanced modal batches its own apply.
 *
 * Layout:
 *   Mobile  (<sm): a 6-column grid of two rows: location (4) and rooms
 *                  (2); then the kind and the price sharing four columns
 *                  by their words, and the filters button (2).
 *   Desktop (sm+): horizontal flex-wrap row (auto-width chips).
 *
 * Mobile bottom sheet:
 *   Opening any chip on mobile renders a slide-up bottom sheet with a
 *   backdrop, a header row (the title), the chip content, and a sticky
 *   footer (Reset + Apply). Animation mirrors ListingDetailPanel:
 *   `entered` state gated on rAF, translate-y-full -> translate-y-0,
 *   200 ms ease-out. Body scroll is locked while any sheet is open.
 *   On desktop the chip renders the existing absolute dropdown popover.
 *
 * Dark mode: this app inverts Tailwind gray/tinted CSS variables in a
 * `.dark` block. Use ONLY bare light-mode classes - NO dark: variants.
 */

import { drawnAreaCount } from "@/lib/draw-area";
import { prefersReducedMotion } from "@/lib/motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { ChevronDown, ListFilter, Check } from "lucide-react";
import { useLocale, useT } from "@/lib/locale-context";
import { haptic } from "@/lib/haptics";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { useBackToClose } from "@/lib/use-back-to-close";
import { isSeparate, kindChangePatch, kindChipKey, kindsOf, LISTING_KINDS, type ListingKind as Kind } from "@/lib/property-kinds";
import SheetGrip from "@/components/SheetGrip";
import ListingsLocationPicker from "./ListingsLocationPicker";
import { showLabel, useLiveCount, withPatch, type ParamPatch } from "@/lib/listings-count";
import { KIND_ICON } from "./filter-icons";
import { FILTER_COUNT, SEGMENT_OFF, SEGMENT_ON, SELECTED, UNSELECTED } from "@/lib/ui-classes";


const ROOMS = ["1", "2", "3", "4", "5"];
const KIND_OPTIONS = LISTING_KINDS;

// ── Mobile breakpoint hook ────────────────────────────────────────────
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

// ── Props ─────────────────────────────────────────────────────────────
interface Props {
  searchParams: ReadonlyURLSearchParams;
  /** Instant merge into the URL (preserves view/sort/other filters). */
  onMerge: (updates: Record<string, string | string[] | null>) => void;
  /** Open the full advanced-search modal. */
  onOpenAdvanced: () => void;
  /** Number of active filters (drives the Filtrlər badge). */
  activeCount: number;
  /** When true, the Location picker is hidden (e.g. on the map page
   *  where the user draws a radius instead of picking from a list). */
  hideLocation?: boolean;
  /** The developers' offers page: every offer is a sale of a flat in a
   *  new building, so the transaction and the kind chips have nothing
   *  to choose; the price and the filters button share their row. */
  salesOfNewFlatsOnly?: boolean;
  /** An area landing page (owner, 2026-10-31: its top was "overcrowded"):
   *  only the price, the rooms (a plot's sot, a shop's m²) and the filters
   *  button, sized to their words in one row. The deal, the kind and the
   *  place are the page itself; its H1 says them and the sheet holds them. */
  compact?: boolean;
}

// ── Bottom sheet animation constant ──────────────────────────────────
const SHEET_MS = 200;

// ── The "Filtrlər" button ─────────────────────────────────────────────
/**
 * The entry to the full filter sheet, with the count of set filters as a
 * badge. Built once here so the bar and the Telegram Mini App's one-row
 * bar (place, filters, sort) show the same pill.
 */
export function FiltersButton({
  onClick,
  activeCount,
  className = "",
}: {
  onClick: () => void;
  activeCount: number;
  className?: string;
}) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors shrink-0 ${className}`}
    >
      <ListFilter className="w-5 h-5" />
      {/* "Filtrlər" on a phone (its one button over the list), "Bütün
          filtrlər" beside the desktop bar's own pills. */}
      <span className="sm:hidden">{t("listings.filter.more.short")}</span>
      <span className="hidden sm:inline">{t("listings.filter.all")}</span>
      {activeCount > 0 && (
        <span className={`absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-xs font-bold tabular-nums ${FILTER_COUNT}`}>
          {activeCount}
        </span>
      )}
    </button>
  );
}

// ── Dropdown / bottom-sheet chip primitive ────────────────────────────
/**
 * Wraps arbitrary filter content in either:
 *   - desktop: an absolute dropdown popover (existing behaviour)
 *   - mobile:  a slide-up bottom sheet with backdrop, header, and footer
 *
 * `onClose`    - fired when the popover/sheet closes (price commit hook)
 * `onReset`    - fired by the mobile Reset button (clears the chip)
 * `sheetTitle` - shown in the mobile sheet header (usually same as label)
 * `panelWidth` - desktop dropdown width class
 */
function Chip({
  label,
  active,
  className = "",
  grow = "sm:flex-1",
  panelWidth = "w-64",
  sheetTitle,
  onClose,
  onReset,
  onApply,
  base,
  pending,
  children,
}: {
  label: React.ReactNode;
  active: boolean;
  /** Grid placement on phones (the bar is a 6-column grid there). */
  className?: string;
  /** Share of the desktop row (sm:flex-*), so chips size by content. */
  grow?: string;
  panelWidth?: string;
  sheetTitle?: string;
  onClose?: () => void;
  onReset?: () => void;
  /** Phone sheet "Tətbiq et": commit a staged draft before closing. */
  onApply?: () => void;
  /** The page's search and this chip's staged change: the phone sheet's
   *  bottom button is the live "N elanı göstərin" for them. */
  base?: ReadonlyURLSearchParams;
  pending?: ParamPatch;
  children: React.ReactNode;
}) {
  const t = useT();
  const { locale } = useLocale();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const desktopRef = useRef<HTMLDivElement>(null);
  const liveCount = useLiveCount(open && isMobile && base ? withPatch(base, pending ?? {}) : null);

  // Close helper (shared between desktop + mobile paths). Closing applies
  // what the chip staged (onClose commits it); the reset closes without.
  const shut = useCallback((apply: boolean) => {
    if (isMobile) {
      setEntered(false);
      setTimeout(() => setOpen(false), SHEET_MS);
    } else {
      setOpen(false);
    }
    if (apply) onClose?.();
  }, [isMobile, onClose]);
  const close = useCallback(() => shut(true), [shut]);

  // Desktop: close on outside click
  useEffect(() => {
    if (!open || isMobile) return;
    function onDoc(e: MouseEvent) {
      if (desktopRef.current && !desktopRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, isMobile, close]);

  // Mobile: enter animation via rAF (mirrors ListingDetailPanel)
  useEffect(() => {
    if (!open || !isMobile) return;
    const raf = requestAnimationFrame(() => {
      // The closed position must be computed before the open one, or the
      // transition has nothing to start from: a WebView that mounts and
      // opens the sheet within one frame showed it in place, no slide
      // (Telegram, 2026-10-23). Reading layout here forces that first
      // computation.
      void document.body.offsetHeight;
      setEntered(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [open, isMobile]);

  // Mobile: drag the sheet header down to dismiss.
  const { offset: dragOffset, dragging, handleProps, panelRef } = useSheetDrag(close, open);
  // Back (the phone's, or Telegram's in the Mini App) closes the sheet.
  useBackToClose(open && isMobile, close);

  // Body scroll lock while mobile sheet is open
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, isMobile]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  function toggle() {
    if (open) {
      close();
    } else {
      setOpen(true);
    }
  }

  const triggerCls = [
    // A hair less padding on phones, so the kind and the price fit whole
    // on a 375 px screen.
    "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-sm font-medium transition-colors sm:px-3",
    "w-full justify-between",
    // A set filter: a faint brand tint (owner, 2026-10-31, option D).
    active ? SELECTED : UNSELECTED,
  ].join(" ");

  return (
    <div ref={desktopRef} className={`relative sm:min-w-0 ${grow} ${className}`}>
      <button type="button" onClick={toggle} className={triggerCls}>
        <span className="min-w-0 truncate">{label}</span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform ${open && !isMobile ? "rotate-180" : ""}`}
        />
      </button>

      {/* Desktop: inline absolute dropdown - pops in from the trigger
          (scale + fade from top-left) instead of snapping. */}
      {open && !isMobile && (
        <div
          className={`dropdown-enter absolute z-30 left-0 mt-2 ${panelWidth} max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-xl`}
        >
          {children}
        </div>
      )}

      {/* Mobile: bottom sheet - a REAL portal to document.body. `fixed`
          alone isn't enough: ancestors with backdrop-filter/transform (the
          map page's frosted filter island) become the containing block,
          which made the sheet rise from the island instead of the screen
          bottom. */}
      {open && isMobile && typeof document !== "undefined" && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={close}
            aria-hidden="true"
            style={{
              opacity: entered ? 1 : 0,
              transition: `opacity ${SHEET_MS}ms ease-out`,
            }}
            className="fixed inset-0 z-[1190] bg-black/40"
          />

          {/* Sheet */}
          <div ref={panelRef}
            style={{
              transform: entered ? `translateY(${dragOffset}px)` : prefersReducedMotion() ? "none" : "translateY(100%)",
              opacity: entered ? 1 : prefersReducedMotion() ? 0 : 1,
              transition: dragging ? "none" : `transform ${SHEET_MS}ms ease-out, opacity ${SHEET_MS}ms ease-out`,
            }}
            className="fixed inset-x-0 bottom-0 z-[1200] rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl"
          >
            {/* Header - drag down to dismiss. */}
            <div {...handleProps} className="flex items-center justify-between mb-4 touch-none">
              <SheetGrip />
              <span className="text-sm font-semibold text-gray-900">
                {sheetTitle ?? label}
              </span>
              {/* No visible X on a phone sheet (owner, 2026-10-23): a swipe down, a tap outside, Back or Escape close it; screen readers keep the button. */}
              <button type="button" onClick={close} className="sr-only">
                {t("listings.detail.close")}
              </button>
            </div>

            {/* Content */}
            <div className="pb-2">{children}</div>

            {/* Sticky footer: "Sıfırlayın" on the left (owner, 2026-10-29),
                the live count as the button. Closing the sheet applies too
                (a swipe, a tap outside, Back). */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
              {onReset && (
                <button
                  type="button"
                  onClick={() => { onReset(); shut(false); }}
                  className="shrink-0 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300"
                >
                  {t("listings.filter.reset")}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  haptic(8);
                  onApply?.();
                  close();
                }}
                className="flex-1 rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold tabular-nums text-white hover:bg-indigo-700 transition-colors"
              >
                {base ? showLabel(t, locale, liveCount) : t("listings.filter.apply")}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

// ── Main bar ──────────────────────────────────────────────────────────
export default function ListingsFilterBar({
  searchParams,
  onMerge,
  onOpenAdvanced,
  activeCount,
  hideLocation = false,
  salesOfNewFlatsOnly = false,
  compact = false,
}: Props) {
  const t = useT();
  const isMobile = useIsMobile();

  // Current URL-driven values
  const leased = searchParams.get("leased") ?? "";
  const listingKind = searchParams.getAll("listing_kind");
  const rooms = searchParams.getAll("rooms");
  // The kind is exclusive: a flat, a house, land (owner, 2026-10-05), a
  // commercial space or a garage (2026-10-21). New/old build is the
  // flat's sub-option, kept in listing_kind; the old house/land values of
  // listing_kind are read as the kind. A shop or a garage carries its own
  // sub-kinds (property_kind: office, shop, ...; garage, parking).
  const categoryParam = searchParams.get("category") ?? "";
  const kind: Kind =
    (LISTING_KINDS as readonly string[]).includes(categoryParam) ? (categoryParam as Kind)
    : listingKind.includes("land") ? "land" : listingKind.includes("house") ? "house" : "";
  const separate = isSeparate(kind);
  const propertyKinds = searchParams.getAll("property_kind");
  const build = listingKind.filter((k) => k === "new" || k === "old");
  // A build is a flat's: a link with only the build shows the flat picked.
  const shownKind: Kind = kind || (build.length ? "apartment" : "");
  const sotMinUrl = searchParams.get("sot_min") ?? "", sotMaxUrl = searchParams.get("sot_max") ?? "";
  const [sotMin, setSotMin] = useState(sotMinUrl), [sotMax, setSotMax] = useState(sotMaxUrl);
  useEffect(() => { setSotMin(sotMinUrl); setSotMax(sotMaxUrl); }, [sotMinUrl, sotMaxUrl]);
  // A shop's or a garage's size: the m² range sits where rooms sit for a
  // home (land does the same with sot), committed when the panel closes.
  const areaMinUrl = searchParams.get("area_min") ?? "", areaMaxUrl = searchParams.get("area_max") ?? "";
  const [areaMin, setAreaMin] = useState(areaMinUrl), [areaMax, setAreaMax] = useState(areaMaxUrl);
  useEffect(() => { setAreaMin(areaMinUrl); setAreaMax(areaMaxUrl); }, [areaMinUrl, areaMaxUrl]);
  const setKind = (k: Kind) => {
    // Changing the kind drops what no longer applies (kindChangePatch).
    onMerge(kindChangePatch(k, build, searchParams.get("sort")));
  };
  // The kind's second level (owner, 2026-10-29): one subtype or none
  // ("Fərq etməz"; both picked by an older link reads as none).
  const subtypes: { value: string; label: string }[] = shownKind === "apartment"
    ? [{ value: "new", label: t("listings.filter.building_type.new") }, { value: "old", label: t("listings.filter.building_type.old") }]
    : separate ? kindsOf(kind).map((k) => ({ value: k, label: t(kindChipKey(k)) })) : [];
  const subtypeValue = shownKind === "apartment" ? (build.length === 1 ? build[0] : "") : (propertyKinds.length === 1 ? propertyKinds[0] : "");
  const pickSubtype = (v: string) => onMerge(shownKind === "apartment"
    ? { category: "apartment", listing_kind: v ? [v] : null }
    : { property_kind: v ? [v] : null });

  // Phone sheets stage multi-select changes and commit on "Tətbiq et"
  // (owner, 2026-09-20: picking several options was impossible when
  // every tap applied at once). Desktop popovers still apply per click.
  // The drafts re-sync from the URL whenever it changes, so a dismissed
  // sheet drops its half-made picks and an applied one lands cleanly.
  const [draftRooms, setDraftRooms] = useState<string[]>(rooms);
  const roomsKey = rooms.join(",");
  useEffect(() => { setDraftRooms(roomsKey ? roomsKey.split(",") : []); }, [roomsKey]);
  // One-shot attention pulse on the chip a search-bar pick just landed in
  // (the bar closes its panel and fires this).
  const [pulse, setPulse] = useState<string | null>(null);
  useEffect(() => {
    let timer: number | null = null;
    const onPulse = (e: Event) => {
      const target = (e as CustomEvent<{ target?: string }>).detail?.target ?? null;
      setPulse(null);
      requestAnimationFrame(() => setPulse(target));
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => setPulse(null), 600);
    };
    window.addEventListener("menzil:filter-pulse", onPulse);
    return () => { window.removeEventListener("menzil:filter-pulse", onPulse); if (timer) window.clearTimeout(timer); };
  }, []);
  const pulseCls = (key: string) => (pulse === key ? " filter-pulse" : "");
  const roomsSel = isMobile ? draftRooms : rooms;
  const revertDrafts = () => { setDraftRooms(rooms); };
  const priceMinUrl = searchParams.get("price_min") ?? "";
  const priceMaxUrl = searchParams.get("price_max") ?? "";
  const locationIds = searchParams.getAll("location_ids");
  const complexSlugs = searchParams.getAll("complex_slug");

  // Price popover holds local state; commits on close
  const [priceMin, setPriceMin] = useState(priceMinUrl);
  const [priceMax, setPriceMax] = useState(priceMaxUrl);
  useEffect(() => {
    setPriceMin(priceMinUrl);
    setPriceMax(priceMaxUrl);
  }, [priceMinUrl, priceMaxUrl]);

  function commitPrice() {
    // A reversed range (min above max) is a typo, not a request for an
    // empty page: swap the two before applying.
    let lo = priceMin, hi = priceMax;
    if (lo && hi && Number(lo) > Number(hi)) {
      [lo, hi] = [hi, lo];
      setPriceMin(lo);
      setPriceMax(hi);
    }
    if (lo === priceMinUrl && hi === priceMaxUrl) return;
    onMerge({ price_min: lo || null, price_max: hi || null });
  }

  function toggleArray(current: string[], v: string): string[] {
    return current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
  }

  // ── Chip labels ──────────────────────────────────────────────────
  // Transaction defaults to PURCHASE: an absent/false `leased` reads as
  // sale; `leased=all` is the explicit "everything" widening.
  const transactionLabel =
    leased === "true" ? t("listings.filter.transaction.rent")
    : leased === "all" ? t("listings.filter.transaction.both")
    : t("listings.filter.transaction.sale");

  // Phones get the short word ("Otaq", "Növ", "Qiymət") so three chips
  // share one row; desktop keeps the full label.
  const dual = (full: string, short: string) => (
    <>
      <span className="lg:hidden">{short}</span>
      <span className="hidden lg:inline">{full}</span>
    </>
  );

  const kindName = shownKind ? t(`listings.filter.category.${shownKind}` as const) : "";
  const buildName = build.length === 1 ? (build[0] === "new" ? t("listings.filter.building_type.new") : t("listings.filter.building_type.old"))
    // One kind of a shop or a garage picked names it beside the category.
    : separate && propertyKinds.length === 1 ? t(kindChipKey(propertyKinds[0])) : "";
  // On a phone one picked kind stands for the whole label ("Ofis"); the
  // category alone takes its phone word (ru "Коммерция").
  const listingKindLabel = shownKind
    ? dual(buildName ? `${kindName} · ${buildName}` : kindName,
      separate && buildName ? buildName : kind === "commercial" ? t("listings.filter.category.commercial.short") : kindName)
    : dual(t("listings.filter.category"), t("listings.filter.category.short"));
  const sotLabel = sotMinUrl || sotMaxUrl
    ? `${sotMinUrl || "0"}-${sotMaxUrl || "∞"} sot`
    : dual(t("listings.filter.sot"), t("listings.filter.sot.short"));
  const areaLabel = areaMinUrl || areaMaxUrl
    ? `${areaMinUrl || "0"}-${areaMaxUrl || "∞"} m²`
    : dual(t("listings.filter.area"), t("listings.filter.area.short"));

  const roomsLabel = rooms.length
    ? [...rooms].sort((a, b) => Number(a) - Number(b)).map((r) => (r === "5" ? t("listings.filter.rooms.5plus") : r)).join(", ")
    : dual(t("listings.filter.rooms"), t("listings.filter.rooms.short"));

  // Thousand-grouped display ("250 000") for the chip label + price
  // inputs - non-breaking space so a grouped number never wraps.
  const groupNum = (v: string) =>
    v.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  // Sanitize a typed price: digits only, no leading zeros, max 8 digits
  // (99 999 999 AZN - nothing in Baku costs more).
  const cleanPrice = (v: string) =>
    v.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 8);

  // One end alone reads "≥ 100 000" / "≤ 150 000", not "0 - 150 000"
  // (owner, 2026-10-31); both ends keep the hyphen.
  const priceLabel =
    priceMinUrl && priceMaxUrl
      ? `${groupNum(priceMinUrl)} - ${groupNum(priceMaxUrl)}`
      : priceMinUrl
        ? `≥\u00a0${groupNum(priceMinUrl)}`
        : priceMaxUrl
          ? `≤\u00a0${groupNum(priceMaxUrl)}`
          : dual(t("listings.filter.price"), t("listings.filter.price.short"));

  // ── Style helpers ────────────────────────────────────────────────
  const segBtn = (selected: boolean) =>
    `flex-1 rounded-lg border py-2 text-sm font-medium transition-colors ${selected ? SELECTED : UNSELECTED}`;

  // The kind popover's types: sized to their words, each with its icon.
  const typeBtn = (selected: boolean) =>
    `inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${selected ? SELECTED : UNSELECTED}`;

  const pillBtn = (selected: boolean) =>
    `rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${selected ? SELECTED : UNSELECTED}`;

  // The price chip and the rooms slot, placed by the caller: the full
  // bar's grid cells or the compact row of an area landing page.
  const priceChip = (className: string, grow = "") => (
    <Chip
      grow={grow}
      className={className}
      label={priceLabel}
      active={!!(priceMinUrl || priceMaxUrl)}
      panelWidth="w-64"
      sheetTitle={t("listings.filter.price")}
      base={searchParams}
      pending={{ price_min: priceMin || null, price_max: priceMax || null }}
      onClose={commitPrice}
      onReset={() => {
        setPriceMin("");
        setPriceMax("");
        onMerge({ price_min: null, price_max: null });
      }}
    >
      <p className="hidden sm:block mb-2 text-xs font-semibold text-gray-500">
        {t("listings.filter.price")}
      </p>
      <div className="flex items-center gap-2">
        {/* text + inputMode=numeric (not type=number) so the value can
            display thousand groups ("250 000") while the state keeps
            raw digits - number inputs reject formatted strings. */}
        <input
          type="text"
          inputMode="numeric"
          value={groupNum(priceMin)}
          onChange={(e) => setPriceMin(cleanPrice(e.target.value))}
          onKeyDown={(e) => { if (e.key === "Enter") commitPrice(); }}
          placeholder={t("listings.filter.price.min")}
          className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white text-gray-800 px-3 py-2 text-sm tabular-nums placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <span className="text-gray-400">-</span>
        <input
          type="text"
          inputMode="numeric"
          value={groupNum(priceMax)}
          onChange={(e) => setPriceMax(cleanPrice(e.target.value))}
          onKeyDown={(e) => { if (e.key === "Enter") commitPrice(); }}
          placeholder={t("listings.filter.price.max")}
          className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white text-gray-800 px-3 py-2 text-sm tabular-nums placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </Chip>
  );
  const sizeChip = (className: string, grow = "") => (
    separate ? (
      <Chip
        grow={grow}
        className={className}
        label={areaLabel}
        active={!!(areaMinUrl || areaMaxUrl)}
        panelWidth="w-64"
        sheetTitle={t("listings.filter.area")}
        base={searchParams}
        pending={{ area_min: areaMin || null, area_max: areaMax || null }}
        onClose={() => { if (areaMin !== areaMinUrl || areaMax !== areaMaxUrl) onMerge({ area_min: areaMin || null, area_max: areaMax || null }); }}
        onReset={() => { setAreaMin(""); setAreaMax(""); onMerge({ area_min: null, area_max: null }); }}
      >
        <p className="hidden sm:block mb-2 text-xs font-semibold text-gray-500">{t("listings.filter.area")}</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="text" inputMode="numeric" value={areaMin} onChange={(e) => setAreaMin(e.target.value.replace(/[^\d.]/g, ""))} placeholder="min" className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm tabular-nums text-gray-900 focus:border-indigo-400 focus:outline-none" aria-label={`${t("listings.filter.area")} min`} />
          <input type="text" inputMode="numeric" value={areaMax} onChange={(e) => setAreaMax(e.target.value.replace(/[^\d.]/g, ""))} placeholder="max" className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm tabular-nums text-gray-900 focus:border-indigo-400 focus:outline-none" aria-label={`${t("listings.filter.area")} max`} />
        </div>
      </Chip>
    ) : kind === "land" ? (
      <Chip
        grow={grow}
        className={className}
        label={sotLabel}
        active={!!(sotMinUrl || sotMaxUrl)}
        panelWidth="w-64"
        sheetTitle={t("listings.filter.sot")}
        base={searchParams}
        pending={{ sot_min: sotMin || null, sot_max: sotMax || null }}
        onClose={() => { if (sotMin !== sotMinUrl || sotMax !== sotMaxUrl) onMerge({ sot_min: sotMin || null, sot_max: sotMax || null }); }}
        onReset={() => { setSotMin(""); setSotMax(""); onMerge({ sot_min: null, sot_max: null }); }}
      >
        <p className="hidden sm:block mb-2 text-xs font-semibold text-gray-500">{t("listings.filter.sot")}</p>
        <div className="grid grid-cols-2 gap-2">
          <input type="text" inputMode="numeric" value={sotMin} onChange={(e) => setSotMin(e.target.value.replace(/[^\d.]/g, ""))} placeholder="min" className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm tabular-nums text-gray-900 focus:border-indigo-400 focus:outline-none" aria-label={`${t("listings.filter.sot")} min`} />
          <input type="text" inputMode="numeric" value={sotMax} onChange={(e) => setSotMax(e.target.value.replace(/[^\d.]/g, ""))} placeholder="max" className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm tabular-nums text-gray-900 focus:border-indigo-400 focus:outline-none" aria-label={`${t("listings.filter.sot")} max`} />
        </div>
      </Chip>
    ) : (
      <Chip
        grow={grow}
        className={className}
        label={roomsLabel}
        active={rooms.length > 0}
        panelWidth="w-64"
        sheetTitle={t("listings.filter.rooms")}
        base={searchParams}
        pending={{ rooms: draftRooms }}
        onReset={() => { revertDrafts(); onMerge({ rooms: null }); }}
        // Closing applies the staged rooms on a phone (a swipe, a tap
        // outside, Back, the button); desktop applies each tap at once.
        onClose={() => { if (isMobile && draftRooms.join(",") !== roomsKey) onMerge({ rooms: draftRooms.length ? draftRooms : null }); }}
      >
        <p className="hidden sm:block mb-2 text-xs font-semibold text-gray-500">
          {t("listings.filter.rooms")}
        </p>
        {/* Each tap toggles that count. */}
        <div className="flex flex-wrap gap-2">
          {ROOMS.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={roomsSel.includes(r)}
              onClick={() => {
                if (isMobile) { setDraftRooms(toggleArray(draftRooms, r)); return; }
                const next = toggleArray(rooms, r);
                onMerge({ rooms: next.length ? next : null });
              }}
              className={pillBtn(roomsSel.includes(r))}
            >
              {r === "5" ? t("listings.filter.rooms.5plus") : r}
            </button>
          ))}
        </div>
      </Chip>
    )
  );

  // An area landing page: one row sized to the words (owner, 2026-10-31).
  if (compact) {
    return (
      <div className="flex min-w-0 items-center gap-2">
        {priceChip(`shrink-0 ${pulseCls("price")}`)}
        {sizeChip(`shrink-0 ${pulseCls("rooms")}`)}
        <FiltersButton onClick={onOpenAdvanced} activeCount={activeCount} />
      </div>
    );
  }

  return (
    /*
     * Mobile: two balanced rows, ordered by how often each filter is
     * actually set (prod tracking, 60 days to 2026-09-19, sessions that
     * set it: location 563, kind 389, rooms 365, price 310, transaction
     * 119). Row 1 = location (half) + rooms (half); row 2 = kind, price
     * and the filters button as thirds, short labels. Transaction (the
     * least used, defaulted to purchase) leaves the phone bar and stays
     * in the filters sheet. Desktop: flex-wrap row, unchanged.
     */
    // Phones: six columns that never stretch to fit a long place name
    // (minmax(0,1fr)); location takes four, rooms two (owner, 2026-10-12:
    // a long location ran into the rooms pill).
    <div className="grid grid-cols-[repeat(6,minmax(0,1fr))] gap-2 sm:flex sm:flex-nowrap sm:items-center sm:gap-2">

      {/* 1. Transaction - defaults to PURCHASE (absent param = sale);
          "Hamısı" widens explicitly via the URL-only `leased=all`.
          Desktop only; phones reach it through the filters sheet. */}
      <div className={salesOfNewFlatsOnly ? "hidden" : "hidden sm:contents"}>
      <Chip
        grow="sm:flex-[0.8]"
        label={transactionLabel}
        active={leased === "true" || leased === "all"}
        panelWidth="w-64"
        sheetTitle={t("listings.filter.transaction")}
        onReset={() => onMerge({ leased: null })}
      >
        <div className="flex gap-2">
          {[
            { label: t("listings.filter.transaction.sale"), value: "" },
            { label: t("listings.filter.transaction.rent"), value: "true" },
            { label: t("listings.filter.transaction.both"), value: "all" },
          ].map(({ label, value }) => (
            <button
              key={value || "sale"}
              type="button"
              onClick={() => onMerge({ leased: value || null })}
              className={segBtn(
                value === ""
                  ? leased === "" || leased === "false"
                  : leased === value,
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </Chip>
      </div>

      {/* Phones: the kind and the price share the row's first two thirds
          by their words, so a long kind ("Kommersiya", "Частный дом")
          is read whole beside a short price (owner, 2026-10-21:
          "Kommer..." was cut). The filters button keeps its third, under
          the rooms pill. Desktop: the wrapper is not there (contents). */}
      <div className={`order-3 ${salesOfNewFlatsOnly ? "col-span-3" : "col-span-4"} flex min-w-0 gap-2 sm:contents`}>
      {/* 2. The kind: a flat, a house or land; under a flat, the build. */}
      {!salesOfNewFlatsOnly && (
      <Chip
        className="min-w-0 flex-auto sm:order-1"
        label={listingKindLabel}
        active={!!shownKind}
        panelWidth="w-96"
        sheetTitle={t("listings.filter.category")}
        base={searchParams}
        pending={kind === "house" ? { sot_min: sotMin || null, sot_max: sotMax || null } : {}}
        onReset={() => setKind("")}
        // A house's plot size is typed right here (feedback ticket 12,
        // 2026-10-15: "no land-area filter for house search"); it commits
        // when the panel closes, like the sot chip does for land.
        onClose={() => { if (kind === "house" && (sotMin !== sotMinUrl || sotMax !== sotMaxUrl)) onMerge({ sot_min: sotMin || null, sot_max: sotMax || null }); }}
      >
        <p className="hidden sm:block mb-2 text-xs font-semibold text-gray-500">{t("listings.filter.category")}</p>
        {/* Two levels, as in the filter sheet (owner, 2026-10-29). Row 1:
            the five types with their icons, sized to their words. */}
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("listings.filter.category")}>
          {KIND_OPTIONS.map((value) => {
            const on = shownKind === value;
            const Icon = KIND_ICON[value];
            return (
              <button key={value} type="button" role="radio" aria-checked={on} onClick={() => setKind(on ? "" : value)} className={typeBtn(on)}>
                <Icon className={`h-3.5 w-3.5 shrink-0 ${on ? "text-gray-900" : "text-gray-400"}`} aria-hidden />
                {t(`listings.filter.category.${value}` as const)}
              </button>
            );
          })}
        </div>
        {/* Row 2, under a clear gap, only for a type with subtypes: the
            light track with "Fərq etməz" first, on the keys it always had
            (a flat's build in listing_kind, the rest in property_kind). A
            house keeps its plot size here; land its hint. */}
        {subtypes.length > 0 ? (
          <div className="mt-3 flex w-full rounded-xl bg-gray-100 p-1" role="radiogroup" aria-label={shownKind === "apartment" ? t("listings.filter.building") : t("listings.filter.property_kind")}>
            {[{ value: "", label: t("listings.filter.any") }, ...subtypes].map((o) => (
              <button
                key={o.value || "any"}
                type="button"
                role="radio"
                aria-checked={subtypeValue === o.value}
                onClick={() => pickSubtype(o.value)}
                className={`flex-1 whitespace-normal leading-tight rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:whitespace-nowrap ${
                  subtypeValue === o.value ? SEGMENT_ON : SEGMENT_OFF
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : shownKind === "house" ? (
          <div className="mt-3">
            <p className="mb-1.5 text-xs font-semibold text-gray-500">{t("listings.filter.sot")}</p>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" inputMode="numeric" value={sotMin} onChange={(e) => setSotMin(e.target.value.replace(/[^\d.]/g, ""))} placeholder="min" aria-label={`${t("listings.filter.sot")} min`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500" />
              <input type="text" inputMode="numeric" value={sotMax} onChange={(e) => setSotMax(e.target.value.replace(/[^\d.]/g, ""))} placeholder="max" aria-label={`${t("listings.filter.sot")} max`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
        ) : shownKind === "land" ? (
          <p className="mt-3 text-xs text-gray-500">{t("listings.filter.kind.land_hint")}</p>
        ) : null}
      </Chip>
      )}

      {/* 4. Price (commits on close) */}
      {priceChip(`min-w-0 flex-auto sm:order-3 ${pulseCls("price")}`, "sm:flex-[1.3]")}
      </div>

      {/* 3. Rooms; for land the same slot holds the plot size in sot, for
          a shop or a garage the floor area in m², so the bar keeps its
          shape. */}
      {sizeChip(`order-2 ${hideLocation ? "col-span-6" : "col-span-2"} min-w-0 sm:order-2 ${pulseCls("rooms")}`, "sm:flex-[0.9]")}

      {/* Viewed lives in the filters sheet only (owner, 2026-10-17: not a
          main filter). */}

      {/* AI valuation (deal tier) lives ONLY in the advanced modal now -
          it was removed from the main bar to keep it compact. */}

      {/* menzil.ai-only toggle chip REMOVED (owner call 2026-07-18:
          premature while first-party inventory is tiny - an almost
          always empty result set one tap away read as broken). The
          ?source=own URL param still works (sitemap + deep links);
          reintroduce a surface once supply justifies it. */}

      {/* Location + Advanced button - full-width row on mobile */}
      <div className={`order-1 col-span-4 min-w-0 items-center gap-2 sm:order-none sm:contents ${hideLocation ? "hidden" : "flex"}`}>
        {/*
         * ListingsLocationPicker is ITSELF a modal (portal'd dialog),
         * so it must NOT be nested inside a Chip popover. Render directly.
         * On mobile it takes flex-1 so it fills the row beside the Filtrlər
         * button; on desktop sm:contents lets it sit inline in the flex row.
         * Hidden on the map page where the user draws a radius instead.
         */}
        {!hideLocation && (
          <div className={`min-w-0 max-sm:flex-1 sm:order-4 sm:flex-[1.7] ${pulseCls("location")}`}>
            <ListingsLocationPicker
              countBase={searchParams}
              selectedIds={locationIds}
              onChange={(ids) => onMerge({ location_ids: ids })}
              // A residence is a place of homes: no residence tab for a
              // shop or a garage.
              selectedComplexSlugs={separate ? undefined : complexSlugs}
              onComplexChange={separate ? undefined : (slugs) => onMerge({ complex_slug: slugs })}
              // One atomic merge: two sequential onMerge calls would
              // each build from the same stale searchParams and the
              // second would wipe the first's keys.
              onApply={(ids, slugs) => onMerge(separate ? { location_ids: ids } : { location_ids: ids, complex_slug: slugs })}
              radiusKm={searchParams.get("center_lat") && Number(searchParams.get("radius_km")) > 0 ? Number(searchParams.get("radius_km")) : null}
              drawnAreas={drawnAreaCount(searchParams.get("draw"))}
            />
          </div>
        )}

        {/* Advanced filters - icon-only trigger (entry to the additional
            filters modal). The active-count badge shows how many advanced
            filters are applied. */}
      </div>
      <div className={`order-5 ${salesOfNewFlatsOnly ? "col-span-3" : "col-span-2"} sm:order-none sm:contents`}>
        <FiltersButton onClick={onOpenAdvanced} activeCount={activeCount} className="w-full sm:order-5 sm:w-auto" />
      </div>
    </div>
  );
}
